import React from 'react';

import { ContainerWithMaxWidth } from '@/core/components/ContainerWithMaxWidth';
import WhiteDonnonsLogo from '@/core/components/WhiteDonnonsLogo';
import type { CategoryDomain } from '@/core/lib/new-architecture/domain/categories.domain';
import Store from '@/core/lib/new-architecture/store';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

export interface FooterProps {
  showCatalogueLinks?: boolean;
}

const Footer: React.FC<FooterProps> = ({ showCatalogueLinks = false }) => {
  const { t } = useTranslationContext(['common']);
  const { data: categories } = Store.categories.useCategories();

  const { Link } = useRouterContext();
  const externalLinks = [
    { key: 'vision', href: new Routes.ExternalRoute('vision'), target: '_blank' },
    // { key: 'taker-chart', href: new Routes.ExternalRoute('') },
    // { key: 'giver-chart', href: new Routes.ExternalRoute('') },
    { key: 'faq', href: new Routes.ExternalRoute('faq'), target: '_blank' },
    { key: 'blog', href: new Routes.ExternalRoute('blog'), target: '_blank' },
    // { key: 'media', href: new Routes.ExternalRoute('') },
    // { key: 'contact-us', href: new Routes.ExternalRoute('') },
    // { key: 'legal-information', href: new Routes.ExternalRoute('') },
    { key: 'cgu', href: new Routes.CguRoute() },
  ];

  const levelOneCategories = categories?.getParentOnlyItems();
  const categoriesAliasForFooter = levelOneCategories?.map(c => c.value.alias);

  let categoriesForFooter: (CategoryDomain | undefined)[] | undefined;

  if (categoriesAliasForFooter) {
    categoriesForFooter = categories?.findManyByAlias(categoriesAliasForFooter);
  }

  const cities = [
    {
      label: 'Bordeaux',
      href: new Routes.CatalogueRoute({ loc: 'Bordeaux-33' }),
    },
    {
      label: 'Marseille',
      href: new Routes.CatalogueRoute({ loc: 'Marseille-13' }),
    },
    {
      label: 'Paris',
      href: new Routes.CatalogueRoute({ loc: 'Paris-75' }),
    },
    {
      label: 'Strasbourg',
      href: new Routes.CatalogueRoute({ loc: 'Strasbourg-67' }),
    },
    {
      label: 'Dijon',
      href: new Routes.CatalogueRoute({ loc: 'Dijon-21' }),
    },
    {
      label: 'Montpellier',
      href: new Routes.CatalogueRoute({ loc: 'Montpellier-34' }),
    },
    {
      label: 'Reims',
      href: new Routes.CatalogueRoute({ loc: 'Reims-51' }),
    },
    {
      label: 'Toulon',
      href: new Routes.CatalogueRoute({ loc: 'Toulon-83' }),
    },
    {
      label: 'Lille',
      href: new Routes.CatalogueRoute({ loc: 'Lille-59' }),
    },
    {
      label: 'Nantes',
      href: new Routes.CatalogueRoute({ loc: 'Nantes-44' }),
    },
    {
      label: 'Rennes',
      href: new Routes.CatalogueRoute({ loc: 'Rennes-35' }),
    },
    {
      label: 'Toulouse',
      href: new Routes.CatalogueRoute({ loc: 'Toulouse-31' }),
    },
    {
      label: 'Lyon',
      href: new Routes.CatalogueRoute({ loc: 'Lyon-69' }),
    },
    {
      label: 'Nice',
      href: new Routes.CatalogueRoute({ loc: 'Nice-06' }),
    },
    {
      label: 'Saint-Étienne',
      href: new Routes.CatalogueRoute({ loc: 'Saint-Etienne-42' }),
    },
  ];

  const departments = [
    {
      label: 'Alpes-Maritimes',
      href: new Routes.CatalogueRoute({ loc: 'Alpes-Maritimes' }),
    },
    {
      label: 'Haute-Garonne',
      href: new Routes.CatalogueRoute({ loc: 'Haute-Garonne' }),
    },
    {
      label: 'Loire-Atlantique',
      href: new Routes.CatalogueRoute({ loc: 'Loire-Atlantique' }),
    },
    {
      label: 'Var',
      href: new Routes.CatalogueRoute({ loc: 'Var' }),
    },
    {
      label: 'Bouches-du-Rhône',
      href: new Routes.CatalogueRoute({ loc: 'Bouches-du-Rhone' }),
    },
    {
      label: 'Hérault',
      href: new Routes.CatalogueRoute({ loc: 'Herault' }),
    },
    {
      label: 'Moselle',
      href: new Routes.CatalogueRoute({ loc: 'Moselle' }),
    },
    {
      label: 'Rhône',
      href: new Routes.CatalogueRoute({ loc: 'Rhone' }),
    },
    {
      label: 'Essonne',
      href: new Routes.CatalogueRoute({ loc: 'Essonne' }),
    },
    {
      label: 'Ille-et-Vilaine',
      href: new Routes.CatalogueRoute({ loc: 'Ille-et-Vilaine' }),
    },
    {
      label: 'Paris',
      href: new Routes.CatalogueRoute({ loc: 'Paris' }),
    },
    {
      label: 'Gironde',
      href: new Routes.CatalogueRoute({ loc: 'Gironde' }),
    },
    {
      label: 'Isère',
      href: new Routes.CatalogueRoute({ loc: 'Isere' }),
    },
    {
      label: 'Pas-de-Calais',
      href: new Routes.CatalogueRoute({ loc: 'Pas-de-Calais' }),
    },
  ];

  const renderSocialMediaOrExternalLinks = (showSocialMedia: boolean) => {
    if (showSocialMedia) {
      return (
        <>
          <div className="mb-4 flex justify-center gap-x-7 lg:mb-0 lg:justify-start">
            <Link href={new Routes.ExternalRoute('facebook')} target="_blank">
              <img src="/assets/socialNetwork/facebook.png" alt={t('social-network.facebook-alt', { ns: 'common' })} />
            </Link>
            <Link href={new Routes.ExternalRoute('instagram')} target="_blank">
              <img src="/assets/socialNetwork/instagram.png" alt={t('social-network.instagram-alt', { ns: 'common' })} />
            </Link>
          </div>
          <div className="flex justify-center gap-x-7 lg:justify-start">
            <Link href={new Routes.ExternalRoute('appStore')} target="_blank" className="w-35">
              <img src="/assets/socialNetwork/app-store.png" alt={t('social-network.app-store-alt', { ns: 'common' })} />
            </Link>
            <Link href={new Routes.ExternalRoute('playStore')} target="_blank" className="w-35">
              <img src="/assets/socialNetwork/google-play.png" alt={t('social-network.play-store-alt', { ns: 'common' })} />
            </Link>
          </div>
        </>
      );
    }
    return (
      <>
        {externalLinks.map(externalLink => (
          <Link key={externalLink.key} href={externalLink.href} className="text-button font-normal text-content-button-primary" target={externalLink.target}>
            {t(`components.footer.${externalLink.key}`, { ns: 'common' })}
          </Link>
        ))}
      </>
    );
  };

  const renderCatalogueLinks = () => (
    <>
      {categoriesForFooter && categoriesForFooter.length > 0 && (
        <div className="border-b border-bg-primary pb-5">
          <p className="mb-4 font-semibold text-content-button-primary">{t('components.footer.categories', { ns: 'common' })}</p>
          <div className="grid grid-cols-1 gap-y-3 lg:grid-cols-4">
            {categoriesForFooter.map(category => {
              if (!category) return null;
              return (
                <Link key={category.label} href={new Routes.CatalogueRoute({ cat: category.alias })} className="text-button font-normal text-content-button-primary">
                  {category.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
      <div className="border-b border-bg-primary py-5">
        <p className="mb-4 font-semibold text-content-button-primary">{t('components.footer.cities', { ns: 'common' })}</p>
        <div className="grid grid-cols-1 gap-y-3 lg:grid-cols-4">
          {cities.map(city => (
            <Link key={city.label} href={city.href} className="text-button font-normal text-content-button-primary">
              {city.label}
            </Link>
          ))}
        </div>
      </div>
      <div className="mb-4 border-b border-bg-primary py-5">
        <p className="mb-4 font-semibold text-content-button-primary">{t('components.footer.departments', { ns: 'common' })}</p>
        <div className="grid grid-cols-1 gap-y-3 lg:grid-cols-4">
          {departments.map(department => (
            <Link key={department.label} href={department.href} className="text-button font-normal text-content-button-primary">
              {department.label}
            </Link>
          ))}
        </div>
      </div>
    </>
  );

  const waitingAllLinksAvailableLinksWithoutCatalogueClass = !!showCatalogueLinks && externalLinks.length < 5 ? 'space-y-4 lg:space-y-0 lg:space-x-4' : 'justify-between';
  const waitingAllLinksAvailableWithCatalogueClass = showCatalogueLinks || externalLinks.length > 4 ? 'justify-between' : 'space-y-3 lg:space-y-0 lg:space-x-8';

  return (
    <footer className="w-full bg-content-secondary py-8 lg:py-12">
      <ContainerWithMaxWidth>
        {showCatalogueLinks && renderCatalogueLinks()}
        <div className={`flex flex-col ${waitingAllLinksAvailableWithCatalogueClass} gap-2 py-2 lg:flex-row`}>{renderSocialMediaOrExternalLinks(showCatalogueLinks)}</div>
        <div className={`mb-8 mt-4 flex flex-col ${waitingAllLinksAvailableLinksWithoutCatalogueClass} border-y border-bg-primary py-4 lg:mb-10 lg:flex-row`}>
          {renderSocialMediaOrExternalLinks(!showCatalogueLinks)}
        </div>
        <Link href={new Routes.HomeRoute()}>
          <WhiteDonnonsLogo />
        </Link>
      </ContainerWithMaxWidth>
    </footer>
  );
};

export default Footer;
